import { __awaiter } from '../node_modules/tslib/tslib.es6.js';

/** Error thrown when rate limit is hit */
class RateLimitError extends Error {
    constructor(message = 'Too many requests. Please wait a moment before trying again.') {
        super(message);
        this.name = 'RateLimitError';
    }
}
// Default base URL for API requests (relative path for landing-page)
const DEFAULT_API_BASE_URL = '/api-rewards';
const streamApi = ({ prompt, base64Image, imageMimeType, imageUrl, brandId, appConfigId, onChunk, onEvent, onStatusChange, onComplete, onError, conversationId, onConversationId, onNewProductMetadata, onFeaturedProduct, onStoreLocator, onOrders, onSurveys, headersToInject, apiBaseUrl = DEFAULT_API_BASE_URL, signal, }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    try {
        const headers = Object.assign(Object.assign({}, headersToInject), { Accept: 'text/event-stream', 'Content-Type': 'application/json' });
        const response = yield fetch(`${apiBaseUrl}/concierge/agent/stream`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                brandId,
                appConfigId,
                prompt,
                imageData: base64Image,
                imageMimeType,
                imageUrl,
                conversationId,
            }),
            signal,
        });
        if (!response.ok) {
            if (response.status === 429) {
                throw new RateLimitError();
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const reader = (_a = response.body) === null || _a === void 0 ? void 0 : _a.getReader();
        if (!reader) {
            throw new Error('No reader available');
        }
        const decoder = new TextDecoder();
        let buffer = '';
        // eslint-disable-next-line no-constant-condition
        streamLoop: while (true) {
            const { done, value } = yield reader.read();
            if (done) {
                onComplete === null || onComplete === void 0 ? void 0 : onComplete();
                break;
            }
            // Decode the chunk and add to buffer
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;
            // Process complete lines
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer
            for (const line of lines) {
                // Skip empty lines and non-data lines
                try {
                    // Handle SSE format with 'data: ' prefix
                    let jsonLine = line;
                    if (line.startsWith('data: ')) {
                        jsonLine = line.slice(6); // Remove 'data: ' prefix
                    }
                    // Skip if empty after removing prefix
                    if (!jsonLine.trim()) {
                        continue;
                    }
                    // Parse the JSON message
                    const message = JSON.parse(jsonLine);
                    switch (message.type) {
                        case 'conversation_id':
                            onConversationId === null || onConversationId === void 0 ? void 0 : onConversationId(message.conversation_id);
                            break;
                        case 'status':
                            onStatusChange(message.message || '');
                            break;
                        case 'content':
                            // Stream each chunk as it arrives
                            const content = message.content || '';
                            onChunk(content);
                            break;
                        case 'products':
                            const productMetadata = (message.content || []);
                            // Call the new callback with product metadata
                            onNewProductMetadata === null || onNewProductMetadata === void 0 ? void 0 : onNewProductMetadata(productMetadata);
                            // Also send as chunk for backward compatibility
                            onChunk(JSON.stringify(productMetadata));
                            break;
                        case 'featured_product':
                            // Handle featured product with question
                            if (message.type === 'featured_product') {
                                onFeaturedProduct === null || onFeaturedProduct === void 0 ? void 0 : onFeaturedProduct(message.content, message.question);
                            }
                            break;
                        case 'follow_up_queries':
                            onEvent({
                                type: 'follow_up_queries',
                                name: 'follow_up_queries',
                                data: message.content || [],
                            });
                            break;
                        case 'store_locator':
                            // Handle store locator event - render the StoreLocatorWidget
                            if (message.type === 'store_locator' && ((_b = message.data) === null || _b === void 0 ? void 0 : _b.stacklineSku)) {
                                onStoreLocator === null || onStoreLocator === void 0 ? void 0 : onStoreLocator(message.data.stacklineSku);
                            }
                            break;
                        case 'orders':
                            // Handle orders event - render order cards
                            if (message.type === 'orders' && message.content) {
                                onOrders === null || onOrders === void 0 ? void 0 : onOrders(message.content);
                            }
                            break;
                        case 'surveys':
                            // Handle surveys event - render survey cards (like products)
                            if (message.type === 'surveys' && message.content) {
                                onSurveys === null || onSurveys === void 0 ? void 0 : onSurveys(message.content);
                            }
                            break;
                        case 'event':
                            onEvent(message);
                            break;
                        case 'end':
                            onChunk('\n\n');
                            // Don't call onComplete here - let the stream finish naturally
                            // The stream will complete after this message
                            break;
                        case 'complete':
                            // Explicit completion signal from server
                            // Clear status immediately to hide "Thinking" indicator
                            onStatusChange('');
                            onComplete === null || onComplete === void 0 ? void 0 : onComplete();
                            // Break out of the stream loop to trigger finally block and set isActivelyStreaming to false
                            break streamLoop;
                        default:
                            console.warn('Unknown message type:', message);
                            break;
                    }
                }
                catch (parseError) {
                    // If not valid JSON, skip the line (don't display raw data)
                    console.warn('Failed to parse line as JSON, skipping:', line);
                    continue;
                }
            }
        }
    }
    catch (error) {
        if (error.name === 'AbortError') {
            // Stream was intentionally cancelled - don't call onComplete or onError.
            // The caller who aborted is responsible for state cleanup.
            return;
        }
        else {
            onError === null || onError === void 0 ? void 0 : onError(error);
        }
    }
});
// Flag to enable hardcoded conversation for testing
// Set to true to use hardcoded conversation, false to use normal API
const USE_HARDCODED_CONVERSATION = false;
const loadConversation = ({ conversationId, headersToInject, apiBaseUrl = DEFAULT_API_BASE_URL, }) => __awaiter(void 0, void 0, void 0, function* () {
    // If flag is disabled and no conversationId, return null
    if (!conversationId) {
        return null;
    }
    try {
        const headers = Object.assign(Object.assign({}, headersToInject), { 'Content-Type': 'application/json' });
        const response = yield fetch(`${apiBaseUrl}/concierge/conversation/${conversationId}`, {
            method: 'GET',
            headers,
        });
        if (!response.ok) {
            if (response.status === 404) {
                return null; // Conversation not found
            }
            if (response.status === 403) {
                // Unauthorized - return null with error
                const data = yield response.json();
                return { error: 'unauthorized', redirect: data.redirect };
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = yield response.json();
        if (data.error) {
            console.error('Error loading conversation:', data.error);
            return null;
        }
        return data;
    }
    catch (error) {
        console.error('Failed to load conversation:', error);
        return null;
    }
});
/**
 * Send feedback (like/dislike) for a message
 */
const sendMessageFeedback = ({ conversationId, messageIndex, feedbackType, headersToInject, apiBaseUrl = DEFAULT_API_BASE_URL, }) => __awaiter(void 0, void 0, void 0, function* () {
    const headers = Object.assign(Object.assign({}, headersToInject), { 'Content-Type': 'application/json' });
    const response = yield fetch(`${apiBaseUrl}/concierge/message/feedback`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            conversationId,
            messageIndex,
            feedbackType,
        }),
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
});
/**
 * Enable or disable sharing for a conversation.
 * When enabled with a messageIndex, creates a checkpoint — non-owners will only see
 * messages up to that index.
 */
const setConversationSharing = ({ conversationId, enabled, messageIndex, headersToInject, apiBaseUrl = DEFAULT_API_BASE_URL, }) => __awaiter(void 0, void 0, void 0, function* () {
    const headers = Object.assign(Object.assign({}, headersToInject), { 'Content-Type': 'application/json' });
    const response = yield fetch(`${apiBaseUrl}/concierge/conversation/${conversationId}/share`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ enabled, messageIndex }),
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
});
/**
 * Fetch product metadata by stacklineSku
 * Uses the concierge product endpoint to get product details for the pinned bar
 */
const fetchProductByStacklineSku = ({ stacklineSku, headersToInject, apiBaseUrl = DEFAULT_API_BASE_URL, }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const headers = Object.assign(Object.assign({}, headersToInject), { 'Content-Type': 'application/json' });
        const response = yield fetch(`${apiBaseUrl}/product/${stacklineSku}`, {
            method: 'GET',
            headers,
        });
        if (!response.ok) {
            if (response.status === 404) {
                return null;
            }
            throw new Error(`Failed to fetch product: ${response.status}`);
        }
        const data = yield response.json();
        return data.product || data;
    }
    catch (error) {
        console.error('Error fetching product by stacklineSku:', error);
        return null;
    }
});

export { RateLimitError, USE_HARDCODED_CONVERSATION, fetchProductByStacklineSku, loadConversation, sendMessageFeedback, setConversationSharing, streamApi };
//# sourceMappingURL=api.js.map
